import {Set as ImmutableSet} from 'immutable';
import * as t from 'io-ts';
import _, {omit} from 'lodash';

import {makeEnumRuntimeType} from '../models/api/types/enumTypes';
import {makeOptional} from '../models/api/types/optionalType';
import {makeImmutableSetType} from '../models/api/types/setType';

/*
 * Types.
 */

export interface ResourceMap<TResource> {
  readonly [resourceId: number]: TResource | undefined;
  readonly [resourceId: string]: TResource | undefined;
}

export interface WritableResourceMap<TResource> {
  [resourceId: number]: TResource | undefined;
  [resourceId: string]: TResource | undefined;
}

// Based on https://redux.js.org/recipes/structuring-reducers/normalizing-state-shape/
export interface ResourceTable<TId extends number | string, TResource> {
  byId: ResourceMap<TResource>;
  deletedIds: ImmutableSet<TId>;
}

export type DeletableResourceTable<TId extends number | string, TResource, TDeletedResource> = ResourceTable<
  TId,
  TResource
> & {deletedById: ResourceMap<TDeletedResource>};

export enum ResourceTableStatusesEnum {
  INITIAL,
  LOADING,
  LOADED,
  FAILED,
}

export const RResourceTableStatus = makeEnumRuntimeType(ResourceTableStatusesEnum);

export interface AsyncStatus {
  status: ResourceTableStatusesEnum;
  isStale: boolean;
  error?: string;
}

export type LazyResourceTable<TId extends number | string, TResource> = ResourceTable<TId, TResource> &
  AsyncStatus;

export enum ResourceMapAffinitiesEnum {
  NEUTRAL = 'NEUTRAL',
  TRANSIENT = 'TRANSIENT',
}

export class ResourceMapType<TIdType extends t.Mixed, TResourceType extends t.Mixed> extends t.ReadonlyType<
  t.Mixed,
  ResourceMap<t.TypeOf<TResourceType>>
> {
  constructor(idType: TIdType, resourceType: TResourceType, affinity: ResourceMapAffinitiesEnum) {
    const baseType = t.readonly(t.record(idType, makeOptional(resourceType)));
    super('resourceMap', baseType.is, baseType.validate, baseType.encode, baseType.type);

    this.affinity = affinity;
  }

  readonly affinity: ResourceMapAffinitiesEnum;
}

/*
 * Factories.
 */

export function makeResourceMap<TIdType extends t.Mixed, TResourceType extends t.Mixed>(
  idType: TIdType,
  resourceType: TResourceType,
) {
  return new ResourceMapType(idType, resourceType, ResourceMapAffinitiesEnum.NEUTRAL);
}

export function makeResourceTable<TIdType extends t.Mixed, TResourceType extends t.Mixed>(
  idType: TIdType,
  resourceType: TResourceType,
) {
  return t.type({
    byId: makeResourceMap(idType, resourceType),
    deletedIds: makeImmutableSetType(idType),
  });
}

export function makeLazyResourceTable<TIdType extends t.Mixed, TResourceType extends t.Mixed>(
  idType: TIdType,
  resourceType: TResourceType,
) {
  return t.intersection([
    makeResourceTable(idType, resourceType),
    t.type({
      status: RResourceTableStatus,
      isStale: t.boolean,
    }),
    t.partial({
      error: t.string,
    }),
  ]);
}

/*
 * Helpers.
 */

export const emptyResourceTable: ResourceTable<any, any> = Object.freeze({
  byId: {},
  deletedIds: ImmutableSet(),
});

export const emptyDeletableResourceTable: DeletableResourceTable<any, any, any> = Object.freeze({
  byId: {},
  deletedIds: ImmutableSet(),
  deletedById: {},
});

export const emptyLazyResourceTable: LazyResourceTable<any, any> = Object.freeze({
  ...emptyResourceTable,
  status: ResourceTableStatusesEnum.INITIAL,
  isStale: false,
});

type ResourceWithId = {id: number | string} | {uid: number | string};

type IdOfResource<TResource extends ResourceWithId> = TResource extends {id: infer TId}
  ? TId
  : TResource extends {uid: infer TId}
    ? TId
    : never;

// If the resource has a `id` or `uid` property, infer the ID type automatically.
export function createResourceTable<TResource extends ResourceWithId>(
  resources: ResourceMap<TResource>,
  deletedIds?: Iterable<IdOfResource<TResource>>,
): ResourceTable<IdOfResource<TResource>, TResource>;
// Otherwise, expose an ID type parameter.
export function createResourceTable<TId extends number | string, TResource>(
  resources: ResourceMap<TResource>,
  deletedIds?: Iterable<TId>,
): ResourceTable<TId, TResource>;
export function createResourceTable<TId extends number | string, TResource>(
  resources: ResourceMap<TResource>,
  deletedIds?: Iterable<TId>,
): ResourceTable<TId, TResource> {
  return {
    ...emptyResourceTable,
    byId: resources,
    deletedIds: deletedIds ? emptyResourceTable.deletedIds.concat(deletedIds) : emptyResourceTable.deletedIds,
  };
}

// If the resource has a `id` or `uid` property, infer the ID type automatically.
export function createDeletableResourceTable<TResource extends ResourceWithId, TDeletedResource = undefined>(
  resources: ResourceMap<TResource>,
  deletedIds?: Iterable<IdOfResource<TResource>>,
  deletedById?: ResourceMap<TDeletedResource>,
): DeletableResourceTable<IdOfResource<TResource>, TResource, TDeletedResource>;
// Otherwise, expose an ID type parameter.
export function createDeletableResourceTable<
  TId extends number | string,
  TResource,
  TDeletedResource = undefined,
>(
  resources: ResourceMap<TResource>,
  deletedIds?: Iterable<TId>,
  deletedById?: ResourceMap<TDeletedResource>,
): DeletableResourceTable<TId, TResource, TDeletedResource>;
export function createDeletableResourceTable<
  TId extends number | string,
  TResource,
  TDeletedResource = undefined,
>(
  resources: ResourceMap<TResource>,
  deletedIds?: Iterable<TId>,
  deletedById?: ResourceMap<TDeletedResource>,
): DeletableResourceTable<TId, TResource, TDeletedResource> {
  return {
    ...emptyDeletableResourceTable,
    byId: resources,
    deletedIds: deletedIds
      ? emptyDeletableResourceTable.deletedIds.concat(deletedIds)
      : emptyDeletableResourceTable.deletedIds,
    deletedById: deletedById
      ? {...emptyDeletableResourceTable.deletedById, ...deletedById}
      : emptyDeletableResourceTable.deletedById,
  };
}

// If the resource has a `id` or `uid` property, infer the ID type automatically.
export function createLazyResourceTable<TResource extends ResourceWithId>(
  resources: ResourceMap<TResource>,
  deletedIds?: Iterable<IdOfResource<TResource>>,
  status?: ResourceTableStatusesEnum,
): LazyResourceTable<IdOfResource<TResource>, TResource>;
// Otherwise, expose an ID type parameter.
export function createLazyResourceTable<TId extends number | string, TResource>(
  resources: ResourceMap<TResource>,
  deletedIds?: Iterable<TId>,
  status?: ResourceTableStatusesEnum,
): LazyResourceTable<TId, TResource>;
export function createLazyResourceTable<TId extends number | string, TResource>(
  resources: ResourceMap<TResource>,
  deletedIds?: Iterable<TId>,
  status: ResourceTableStatusesEnum = ResourceTableStatusesEnum.LOADED,
): LazyResourceTable<TId, TResource> {
  return {
    ...emptyLazyResourceTable,
    ...createResourceTable(resources, deletedIds),
    status,
  };
}

export function mergeResourceTables<
  TId extends number | string,
  TResource,
  TResourceTable extends ResourceTable<TId, TResource> = ResourceTable<TId, TResource>,
>(left: TResourceTable, right: TResourceTable): TResourceTable {
  return {
    ...left,
    ...right,
    byId: {
      ...omit(left.byId, ...right.deletedIds),
      ...omit(right.byId, ...left.deletedIds),
    },
    deletedIds: left.deletedIds.merge(right.deletedIds),
  };
}

export function mergeResourceTablesWithDeleted<
  TId extends number | string,
  TResource,
  TDeletedResource,
  TResourceTable extends DeletableResourceTable<TId, TResource, TDeletedResource> = DeletableResourceTable<
    TId,
    TResource,
    TDeletedResource
  >,
>(left: TResourceTable, right: TResourceTable): TResourceTable {
  return {
    ...left,
    ...right,
    byId: {
      ...omit(left.byId, ...right.deletedIds),
      ...omit(right.byId, ...left.deletedIds),
    },
    deletedIds: left.deletedIds.merge(right.deletedIds),
    deletedById: {...left.deletedById, ...right.deletedById},
  };
}

export function deleteFromResourceTable<
  TId extends number | string,
  TResource,
  TResourceTable extends ResourceTable<TId, TResource> = ResourceTable<TId, TResource>,
>(
  resources: TResourceTable,
  idsOrSingleId: TId | ReadonlyArray<TId>,
  shouldTombstone = true,
): TResourceTable {
  const ids = Array.isArray(idsOrSingleId) ? idsOrSingleId : [idsOrSingleId];
  const newDeletedIds = resources.deletedIds.union(ids);
  return {
    ...resources,
    byId: omit(resources.byId, ...ids),
    deletedIds: shouldTombstone ? newDeletedIds : resources.deletedIds,
  };
}

export function deleteFromDeletableResourceTable<
  TId extends number | string,
  TResource,
  TDeletedResource extends {id: TId},
  TResourceTable extends DeletableResourceTable<TId, TResource, TDeletedResource> = DeletableResourceTable<
    TId,
    TResource,
    TDeletedResource
  >,
>(
  resources: TResourceTable,
  idsOrSingleId: TId | ReadonlyArray<TId>,
  toDeletedResource: (resource: TResource) => TDeletedResource,
  shouldTombstone = true,
): TResourceTable {
  const ids = Array.isArray(idsOrSingleId) ? idsOrSingleId : [idsOrSingleId];
  const newDeletedIds = resources.deletedIds.union(ids);

  const deletedResources = ids.reduce((prev, curr) => {
    const currentResource = resources.byId[curr];
    if (!currentResource) {
      return prev;
    }

    prev.push(toDeletedResource(currentResource));
    return prev;
  }, []);

  const deletedResourceMap = toResourceMapById(deletedResources);

  return {
    ...resources,
    byId: omit(resources.byId, ...ids),
    deletedIds: shouldTombstone ? newDeletedIds : resources.deletedIds,
    deletedById: shouldTombstone ? {...resources.deletedById, ...deletedResourceMap} : resources.deletedById,
  };
}

// Translate an array to a resource map, using the idPredicate function to determine the id of the resource.
export function toResourceMap<T>(
  array: ReadonlyArray<T>,
  idPredicate: (item: T) => number | string,
): ResourceMap<T> {
  return _.keyBy(array, idPredicate);
}

// Translate an array to a resource map, keying by id.
export function toResourceMapById<T extends {id: number | string}>(array: ReadonlyArray<T>): ResourceMap<T> {
  return toResourceMap(array, (item) => item.id);
}
